import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import Seo from "@/components/marketing/Seo";
import { Button } from "@/components/ui/button";
import { useApiClient, ArticleResponse } from "@/lib/api";
import { ArrowLeft, ArrowRight, Loader2, Clock } from "lucide-react";
import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";

// Category color mapping - hash-based generation for consistent, well-separated colors
const getCategoryColor = (category: string): string => {
  // Generate a hash from the category name
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = category.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  // Use hash to generate hue (0-360) with better distribution
  // Multiply by a large prime to spread out values
  const hue = Math.abs(hash * 137.508) % 360;
  
  // Use fixed saturation and lightness for good contrast and readability
  // Saturation: 65% (vibrant but not too intense)
  // Lightness: 45% (dark enough for white text, light enough to be visible)
  return `hsl(${hue}, 65%, 45%)`;
};

// Cookie helpers for tracking read articles (10+ minutes)
const READ_ARTICLES_COOKIE = "coach_u_read_articles";
const READ_TIMES_COOKIE = "coach_u_read_times";

const getReadArticles = (): Set<string> => {
  if (typeof document === "undefined") return new Set();
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${READ_ARTICLES_COOKIE}=`));
  if (!cookie) return new Set();
  try {
    const value = cookie.split("=")[1];
    const slugs = JSON.parse(decodeURIComponent(value));
    return new Set(Array.isArray(slugs) ? slugs : []);
  } catch {
    return new Set();
  }
};

const getReadTimes = (): Record<string, number> => {
  if (typeof document === "undefined") return {};
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${READ_TIMES_COOKIE}=`));
  if (!cookie) return {};
  try {
    const value = cookie.split("=")[1];
    return JSON.parse(decodeURIComponent(value)) || {};
  } catch {
    return {};
  }
};

const updateReadTime = (slug: string, additionalSeconds: number) => {
  if (typeof document === "undefined") return;
  const readTimes = getReadTimes();
  const currentTime = readTimes[slug] || 0;
  const newTime = currentTime + additionalSeconds;
  readTimes[slug] = newTime;

  // If total time >= 10 minutes (600 seconds), mark as read
  if (newTime >= 600) {
    const readArticles = getReadArticles();
    readArticles.add(slug);
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${READ_ARTICLES_COOKIE}=${encodeURIComponent(
      JSON.stringify(Array.from(readArticles))
    )}; expires=${expires.toUTCString()}; path=/`;
  }

  // Update read times cookie
  const expires = new Date();
  expires.setFullYear(expires.getFullYear() + 1);
  document.cookie = `${READ_TIMES_COOKIE}=${encodeURIComponent(
    JSON.stringify(readTimes)
  )}; expires=${expires.toUTCString()}; path=/`;
};

interface ArticleDetailProps {
  mode?: "private" | "public";
  withDashboard?: boolean;
}

const ArticleDetail = ({ mode = "private", withDashboard = true }: ArticleDetailProps) => {
  const { slug } = useParams<{ slug: string }>();
  const { api } = useApiClient();
  const navigate = useNavigate();
  
  const [article, setArticle] = useState<ArticleResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previousArticle, setPreviousArticle] = useState<ArticleResponse | null>(null);
  const [nextArticle, setNextArticle] = useState<ArticleResponse | null>(null);
  const [suggestedArticle, setSuggestedArticle] = useState<ArticleResponse | null>(null);
  const basePath = mode === "public" ? "/public-learn" : "/learn";
  
  const startTimeRef = useRef<number>(Date.now());
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchArticle = useCallback(async () => {
    if (!slug) return;
    
    setIsLoading(true);
    startTimeRef.current = Date.now();
    
    try {
      const response = mode === "public"
        ? await api.getPublicArticle(slug)
        : await api.getArticle(slug);
      setArticle(response);

      // Fetch previous article if exists
      if (response.previous_article) {
        try {
          const prev = mode === "public"
            ? await api.getPublicArticle(response.previous_article)
            : await api.getArticle(response.previous_article);
          setPreviousArticle(prev);
        } catch (error) {
          console.error('Failed to fetch previous article:', error);
        }
      }

      // Fetch next article if exists
      if (response.next_article) {
        try {
          const next = mode === "public"
            ? await api.getPublicArticle(response.next_article)
            : await api.getArticle(response.next_article);
          setNextArticle(next);
        } catch (error) {
          console.error('Failed to fetch next article:', error);
        }
      } else {
        // If no next article, try to suggest a random unread article from the same category
        try {
          const readArticles = getReadArticles();
          const categoryArticles = mode === "public"
            ? await api.getPublicArticles(response.category)
            : await api.getArticles(response.category);
          
          // Filter out current article and already read articles
          const unreadInCategory = categoryArticles.articles.filter(
            (a) =>
              a.slug !== response.slug &&
              !readArticles.has(a.slug) &&
              (mode !== "public" || Boolean(a.is_public))
          );
          
          // Only suggest if there are unread articles in the category
          if (unreadInCategory.length > 0) {
            // Pick a random unread article from the category
            const randomIndex = Math.floor(Math.random() * unreadInCategory.length);
            const randomSlug = unreadInCategory[randomIndex].slug;
            const suggested = mode === "public"
              ? await api.getPublicArticle(randomSlug)
              : await api.getArticle(randomSlug);
            setSuggestedArticle(suggested);
          }
        } catch (error) {
          console.error('Failed to fetch suggested article:', error);
          // Don't show error to user, this is just a nice-to-have feature
        }
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      toast.error('Failed to load article');
      if (mode === "public" && (error as any)?.statusCode === 403) {
        navigate(`/login?returnUrl=${encodeURIComponent(`/learn/${slug}`)}`);
      } else {
        navigate(basePath);
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, slug, navigate, mode, basePath]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    fetchArticle();

    // Track reading time - update every 30 seconds
    intervalRef.current = setInterval(() => {
      if (slug) {
        const elapsed = (Date.now() - startTimeRef.current) / 1000;
        updateReadTime(slug, elapsed);
        startTimeRef.current = Date.now(); // Reset for next interval
      }
    }, 30000); // 30 seconds

    return () => {
      if (intervalRef.current) {
        // Final update on unmount
        if (slug) {
          const elapsed = (Date.now() - startTimeRef.current) / 1000;
          updateReadTime(slug, elapsed);
        }
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchArticle, slug]);

  if (isLoading) {
    const loadingContent = (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
    if (withDashboard) return <DashboardLayout>{loadingContent}</DashboardLayout>;
    return <div className="min-h-screen bg-white">{loadingContent}</div>;
  }

  if (!article) {
    const notFoundContent = (
      <div className="text-center py-12">
        <p className="text-gray-600">Article not found.</p>
        <Button onClick={() => navigate(basePath)} className="mt-4">
          Back to Learn
        </Button>
      </div>
    );
    if (withDashboard) return <DashboardLayout>{notFoundContent}</DashboardLayout>;
    return <div className="min-h-screen bg-white">{notFoundContent}</div>;
  }

  const categoryColor = getCategoryColor(article.category);
  const publicStructuredData = mode === "public" ? {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description || `Coach U article about ${article.title}.`,
    author: {
      "@type": "Organization",
      name: article.author || "HiredCoach Team",
    },
    publisher: {
      "@type": "Organization",
      name: "HiredCoach",
    },
    mainEntityOfPage: `https://hiredcoach.ai/public-learn/${article.slug}`,
  } : undefined;

  const content = (
    <div className="max-w-3xl mx-auto">
        {mode === "public" && (
          <Seo
            title={`${article.title} | Coach U by HiredCoach`}
            description={
              article.description ||
              `Coach U article about ${article.title}, interview prep, and job search strategy.`
            }
            canonicalPath={`/public-learn/${article.slug}`}
            keywords={`${article.title}, Coach U, interview prep, HiredCoach, ${article.category}`}
            ogType="article"
            structuredData={publicStructuredData}
          />
        )}
        <Button
          variant="ghost"
          onClick={() => navigate(basePath)}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Learn
        </Button>

        <article>
          {/* Header */}
          <header className="mb-6">
            <div className="mb-3">
              <span 
                className="px-3 py-1 text-white rounded-full text-sm font-medium"
                style={{ backgroundColor: categoryColor }}
              >
                {article.category}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3">
              {article.title}
            </h1>
            {article.description && (
              <p className="text-lg sm:text-xl text-gray-600 mb-3">
                {article.description}
              </p>
            )}
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                ~8 min read
              </span>
            </div>
          </header>

          {/* Content */}
          <div className="prose prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({node, ...props}) => <h1 className="text-2xl sm:text-3xl font-bold mt-6 mb-3 text-gray-900" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-xl sm:text-2xl font-semibold mt-5 mb-2 text-gray-900" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-lg sm:text-xl font-semibold mt-4 mb-2 text-gray-900" {...props} />,
                p: ({node, ...props}) => <p className="mb-3 text-gray-700 leading-relaxed" {...props} />,
                ul: ({node, ...props}) => <ul className="list-disc list-outside mb-3 space-y-2 text-gray-700 pl-6" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal list-outside mb-3 space-y-2 text-gray-700 pl-6" {...props} />,
                li: ({node, ...props}) => <li className="pl-2" {...props} />,
                code: ({node, className, ...props}: any) => {
                  const isInline = !className;
                  return isInline ? (
                    <code className="bg-gray-100 px-1.5 py-0.5 rounded text-sm font-mono text-gray-800" {...props} />
                  ) : (
                    <code className="block bg-gray-100 p-3 rounded-lg overflow-x-auto text-sm font-mono text-gray-800 mb-3" {...props} />
                  );
                },
                blockquote: ({node, ...props}) => (
                  <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-3" {...props} />
                ),
                a: ({node, ...props}) => (
                  <a className="text-blue-600 hover:text-blue-800 underline" {...props} />
                ),
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {/* Next/Prior Article Navigation */}
          {(previousArticle || nextArticle || suggestedArticle) && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              {(previousArticle || nextArticle) && (
                <div className="flex flex-col sm:flex-row gap-4 mb-4">
                  {previousArticle && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`${basePath}/${previousArticle.slug}`)}
                      className="flex-1 justify-start group min-w-0"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2 group-hover:-translate-x-1 transition-transform flex-shrink-0" />
                      <div className="text-left flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">Previous</div>
                        <div className="font-medium truncate">{previousArticle.title}</div>
                      </div>
                    </Button>
                  )}
                  {nextArticle && (
                    <Button
                      variant="outline"
                      onClick={() => navigate(`${basePath}/${nextArticle.slug}`)}
                      className="flex-1 justify-end group min-w-0"
                    >
                      <div className="text-right flex-1 min-w-0">
                        <div className="text-xs text-gray-500 mb-1">Next</div>
                        <div className="font-medium truncate">{nextArticle.title}</div>
                      </div>
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </Button>
                  )}
                </div>
              )}
              {/* Suggested Article - only show if no next article */}
              {!nextArticle && suggestedArticle && (
                <div 
                  className="bg-gray-50 rounded-lg p-4 border border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors group"
                  onClick={() => navigate(`${basePath}/${suggestedArticle.slug}`)}
                >
                  <div className="text-sm text-gray-600 mb-2">Continue learning</div>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 mb-1">{suggestedArticle.title}</div>
                      {suggestedArticle.description && (
                        <div className="text-sm text-gray-600">
                          {suggestedArticle.description}
                        </div>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-600 group-hover:translate-x-1 transition-transform flex-shrink-0 mt-0.5" />
                  </div>
                </div>
              )}
            </div>
          )}
        </article>
    </div>
  );

  if (withDashboard) {
    return <DashboardLayout>{content}</DashboardLayout>;
  }

  return <div className="px-4 sm:px-6 py-6">{content}</div>;
};

export default ArticleDetail;
